"use strict";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __spreadArrays = (this && this.__spreadArrays) || function () {
    for (var s = 0, i = 0, il = arguments.length; i < il; i++) s += arguments[i].length;
    for (var r = Array(s), k = 0, i = 0; i < il; i++)
        for (var a = arguments[i], j = 0, jl = a.length; j < jl; j++, k++)
            r[k] = a[j];
    return r;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.PojoMap = void 0;
/**
 * Create a PojoMap from a set of key-value pairs.
 * @param entries Key value pairs for creating the map.
 */
function fromEntries(entries) {
    var acc = {};
    for (var _i = 0, entries_1 = entries; _i < entries_1.length; _i++) {
        var _a = entries_1[_i], key = _a[0], value = _a[1];
        acc[key] = value;
    }
    return acc;
}
/**
 * Create a PojoMap from a list of items, indexed by a key selector
 * When there are repeat keys, the last item with the given key will be stored.
 * @param items List of items to index
 * @param keySelector A function that will return the index key for an item.
 * @returns A new PojoMap contiaining the indexed items
 */
function fromIndexing(items, keySelector) {
    var acc = {};
    var i = 0;
    for (var _i = 0, items_1 = items; _i < items_1.length; _i++) {
        var item = items_1[_i];
        var key = keySelector(item, i++);
        acc[key] = item;
    }
    return acc;
}
/**
 * Create a PojoMap from a list of items, grouped by a key selector
 * @param items List of items to group
 * @param keySelector A function that will return the index key for an item.
 * @returns A new PojoMap contiaining the grouped items
 */
function fromGrouping(items, keySelector) {
    var acc = {};
    var i = 0;
    for (var _i = 0, items_2 = items; _i < items_2.length; _i++) {
        var item = items_2[_i];
        var key = keySelector(item, i++);
        var arr = acc[key];
        if (!arr) {
            acc[key] = arr = [];
        }
        arr.push(item);
    }
    return acc;
}
/**
 * Create a PojoMap from a list of items counts, grouped by a key selector
 * @param items List of items to group
 * @param keySelector A function that will return the index key for an item.
 * @returns A new PojoMap contiaining the counts of items by group
 */
function fromCounting(items, keySelector) {
    var _a;
    var acc = {};
    var i = 0;
    for (var _i = 0, items_3 = items; _i < items_3.length; _i++) {
        var item = items_3[_i];
        var key = keySelector(item, i++);
        var count = (_a = acc[key]) !== null && _a !== void 0 ? _a : 0;
        acc[key] = count + 1;
    }
    return acc;
}
/**
 * Get the value stored at a given key in a PojoMap.
 *
 * @param map A PojoMap
 * @param key The key to retrieve the value at
 * @returns The value at the key, or undefined.
 */
function get(map, key) {
    return map[key];
}
/**
 * Check if a key is in a PojoMap.
 *
 * @param map A PojoMap
 * @param key The key to check existence of
 * @returns true if the key exists in the PojoMap, false otherwise.
 */
function has(map, key) {
    return typeof map[key] !== 'undefined';
}
/**
 * Add an entry to a PojoMap, immutably.
 *
 * @param map An existing PojoMap
 * @param key The entry's key
 * @param value The entry's value
 * @returns A new PojoMap containing the original PojoMap and the new entry.
 */
function set(map, key, value) {
    var _a;
    return __assign(__assign({}, map), (_a = {}, _a[key] = value, _a));
}
/**
 * Remove an item from a PojoMap, immutably.
 *
 * @param map An existing PojoMap
 * @param key The key to remove
 * @returns A new PojoMap contianing the original map minus the given key.
 */
function remove(map, key) {
    // Extracting the the key from the PojoMap object via destructuring
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    var _a = map, _b = key, removed = _a[_b], remaining = __rest(_a, [typeof _b === "symbol" ? _b : _b + ""]);
    return remaining;
}
/**
 * Create an empty PojoMap of the given type.
 *
 * @returns A new, empty PojoMap
 */
function empty() {
    return {};
}
/**
 * Get the keys defined in this PojoMap.
 * @param map A PojoMap
 * @returns an array containing all the keys where the map is defined.
 */
function keys(map) {
    var keys = Object.keys(map);
    // For maximum compatibility, filter on the values, in case the user has set some fields to false manually.
    return keys.filter(function (k) { return has(map, k); });
}
/**
 * Get all the values in this PojoMap.
 * @param map A PojoMap
 * @returns an array containing all the values where the map is defined.
 */
function values(map) {
    // Assert that each value is defined, since keys() already filters this.
    return keys(map).map(function (k) { return map[k]; });
}
/**
 * Get all the entries in this PojoMap.
 * @param map A PojoMap
 * @returns an array containing all the key-value pairs where the map is defined.
 */
function entries(map) {
    // Assert that each value is defined, since keys() already filters this.
    return keys(map).map(function (k) { return [k, map[k]]; });
}
/**
 * Get a PojoMap's size (i.e. number of contained values)
 *
 * @param map A PojoMap
 * @returns the number of values contained in a PojoMap
 */
function size(map) {
    return keys(map).length;
}
/**
 * Pick a subset of a PojoMap given a list of keys.
 *
 * @param map A PojoMap
 * @param keys The keys to pick from the PojoMap
 * @returns A copy of the input PojoMap containing only the keys specified
 */
function pick(map, keys) {
    var keyset = new Set(keys);
    return fromEntries(entries(map).filter(function (_a) {
        var key = _a[0];
        return keyset.has(key);
    }));
}
/**
 * Pick a subset of a PojoMap by excluding a list of keys.
 *
 * @param map A PojoMap
 * @param keys The keys to exclude from the PojoMap
 * @returns A copy of the input map with the given keys omitted
 */
function omit(map, keys) {
    var keyset = new Set(keys);
    return fromEntries(entries(map).filter(function (_a) {
        var key = _a[0];
        return !keyset.has(key);
    }));
}
/**
 * Create a new PojoMap based on a supplied transformation function
 *
 * @param map A PojoMap
 * @param transform The function by which to transform the PojoMap's values
 * @returns a new PojoMap, its values transformed as defined by the transform function
 */
function map(map, transform) {
    var originalEntries = entries(map);
    var newEntries = originalEntries.map(function (_a) {
        var key = _a[0], value = _a[1];
        return [key, transform(value, key)];
    });
    return fromEntries(newEntries);
}
/**
 * Create a new PojoMap by combining two existing PojoMaps.
 * If there are duplicate keys, the value from the second map will be used.
 *
 * @param baseMap The base map to copy.
 * @param additionalMap An additional map to copy entries from. This map's values will have precedence in the result.
 * @returns A new map containing keys & values from both input maps.
 */
function union(baseMap, additionalMap) {
    return fromEntries(__spreadArrays(entries(baseMap), entries(additionalMap)));
}
exports.PojoMap = {
    fromEntries: fromEntries,
    fromIndexing: fromIndexing,
    fromGrouping: fromGrouping,
    fromCounting: fromCounting,
    get: get,
    has: has,
    set: set,
    remove: remove,
    empty: empty,
    keys: keys,
    values: values,
    entries: entries,
    size: size,
    pick: pick,
    omit: omit,
    map: map,
    union: union,
};
